// WP-1.19/1.20 (ST-1, OB-1): the settings key→value bag + a single-key setter.
import { settingsResponseSchema } from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type Query,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';
import { AUTH_STATUS_KEY } from './session';

export const SETTINGS_KEY = ['settings'] as const;

export interface UseSettingsOptions {
  /** WB-4: e.g. the WhatsApp link panel polls while awaiting a scan, since Baileys rotates the
   * pairing QR every ~20-60s and react-query otherwise only refetches on mount/focus. */
  refetchInterval?: number | false | ((query: Query<Record<string, unknown>>) => number | false);
}

export function useSettings(options?: UseSettingsOptions): UseQueryResult<Record<string, unknown>> {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: async () => settingsResponseSchema.parse(await api.get('/api/v1/settings')).settings,
    ...(options?.refetchInterval !== undefined ? { refetchInterval: options.refetchInterval } : {}),
  });
}

export function useSetSetting(): UseMutationResult<void, Error, { key: string; value: unknown }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }) => {
      await api.put(`/api/v1/settings/${key}`, { value });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
        // `onboarded` lives in auth status too — refresh it so the gate reacts.
        queryClient.invalidateQueries({ queryKey: AUTH_STATUS_KEY }),
      ]);
    },
  });
}
